/**
 * Поле ввода сообщения для AI чата - минималистичный
 */

import { useState, useRef, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Send, Loader2, Sparkles, Paperclip, Globe, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ModelSelectorModal } from './model-selector-modal';
import { AttachmentPreview } from './attachment-preview';
import { uploadFile } from '@/lib/upload';
import type { PageContext, Attachment } from './types';

/** Форматирование токенов для отображения */
function formatTokens(tokens: number): string {
  if (tokens >= 1000000) {return `${(tokens / 1000000).toFixed(1)}M`;}
  if (tokens >= 1000) {return `${(tokens / 1000).toFixed(0)}K`;}
  return tokens.toString();
}

interface ChatInputProps {
  onSend: (message: string, attachments?: Attachment[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  pageContext?: PageContext;
  placeholder?: string;
  selectedModel?: string | null;
  onModelChange?: (model: string | null) => void;
  // Router Agent
  routerEnabled?: boolean;
  onRouterEnabledChange?: (enabled: boolean) => void;
  routerModel?: string | null;
  onRouterModelChange?: (model: string | null) => void;
  // Web Search
  webSearchEnabled?: boolean;
  onWebSearchEnabledChange?: (enabled: boolean) => void;
  // Knowledge Base
  selectedDocuments?: number[];
  onSelectedDocumentsChange?: (ids: number[]) => void;
  // Контекст
  contextUsagePercent?: number;
  totalTokensUsed?: number;
  contextLimit?: number;
  onClearContext?: () => void;
}

export function ChatInput({
  onSend,
  disabled = false,
  isLoading = false,
  pageContext,
  placeholder = 'Сообщение...',
  selectedModel,
  onModelChange,
  routerEnabled = true,
  onRouterEnabledChange,
  routerModel,
  onRouterModelChange,
  webSearchEnabled = false,
  onWebSearchEnabledChange,
  selectedDocuments = [],
  onSelectedDocumentsChange,
  contextUsagePercent = 0,
  totalTokensUsed = 0,
  contextLimit = 128000,
  onClearContext,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    if ((!message.trim() && attachments.length === 0) || disabled || isLoading) {return;}

    onSend(message.trim(), attachments.length > 0 ? attachments : undefined);
    setMessage('');
    setAttachments([]);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {return;}

    // Для каждого файла создаём временный attachment и загружаем
    for (const file of [...files]) {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // Добавляем как uploading
      const tempAttachment: Attachment = {
        id: tempId,
        url: '',
        mimeType: file.type,
        filename: file.name,
        size: file.size,
        status: 'uploading',
        uploadProgress: 0,
      };
      setAttachments(prev => [...prev, tempAttachment]);

      try {
        const uploadedFile = await uploadFile({
          file,
          access: 'PUBLIC',
        });

        // Обновляем attachment с реальными данными
        setAttachments(prev =>
          prev.map(a =>
            a.id === tempId
              ? {
                  ...a,
                  id: String(uploadedFile.id),
                  url: uploadedFile.url,
                  status: 'ready' as const,
                  uploadProgress: 100,
                }
              : a
          )
        );
      } catch {
        // Помечаем как ошибку
        setAttachments(prev =>
          prev.map(a =>
            a.id === tempId ? { ...a, status: 'error' as const } : a
          )
        );
      }
    }

    // Сбрасываем input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getModelShortName = (modelId: string) => {
    const parts = modelId.split('/');
    const name = parts.at(-1) ?? modelId;
    return name.length > 20 ? name.slice(0, 17) + '...' : name;
  };

  // pageContext передаётся скрыто через onSend, не показываем в UI
  void pageContext;

  // Проверяем, можно ли отправить (есть текст или attachments)
  const canSubmit = (message.trim() || attachments.length > 0) && !disabled && !isLoading;
  // Проверяем, есть ли готовые к отправке attachments
  const hasReadyAttachments = attachments.some(a => a.status === 'ready');
  const hasUploadingAttachments = attachments.some(a => a.status === 'uploading');

  return (
    <div className="px-3 pb-3 pt-2">
      {/* Превью прикреплённых файлов */}
      {attachments.length > 0 && (
        <AttachmentPreview
          attachments={attachments}
          onRemove={handleRemoveAttachment}
          className="mb-2"
        />
      )}

      {/* Поле ввода */}
      <div className="flex items-center gap-2">
        {/* Кнопка прикрепления файла */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isLoading}
          className="h-10 w-10 rounded-xl flex-shrink-0"
        >
          <Paperclip className="h-4 w-4" />
        </Button>

        {/* Скрытый input для файлов */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.doc,.docx,.txt,.md"
          onChange={handleFileSelect}
          className="hidden"
        />

        <Textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(
            'h-10 min-h-[40px] max-h-[40px] resize-none text-sm',
            'rounded-xl px-3 py-2.5 border-muted',
            'focus-visible:ring-1 focus-visible:ring-primary/30'
          )}
          rows={1}
        />

        <Button
          type="button"
          size="icon"
          onClick={handleSubmit}
          disabled={!canSubmit || hasUploadingAttachments}
          className="h-10 w-10 rounded-xl flex-shrink-0"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Нижняя панель с моделью и индикаторами */}
      <div className="mt-2 flex items-center justify-between">
        {/* Левая часть: выбор модели, Web toggle и индикаторы */}
        <div className="flex items-center gap-1.5">
          {/* Выбор модели */}
          <button
            type="button"
            onClick={() => setIsModelModalOpen(true)}
            disabled={isLoading}
            className={cn(
              'inline-flex items-center gap-1.5 h-7 px-2 rounded-lg',
              'text-[11px] text-muted-foreground hover:text-foreground',
              'transition-colors disabled:opacity-50'
            )}
          >
            <Sparkles className="h-3 w-3 flex-shrink-0" />
            <span className="truncate max-w-[140px]">
              {selectedModel ? getModelShortName(selectedModel) : 'Default'}
            </span>
          </button>

          {/* Web Search Toggle */}
          {onWebSearchEnabledChange && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onWebSearchEnabledChange(!webSearchEnabled)}
                  disabled={isLoading}
                  className={cn(
                    'inline-flex items-center gap-1 h-6 px-1.5 rounded-md text-[10px] transition-colors',
                    webSearchEnabled
                      ? 'bg-foreground text-background'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Globe className="h-3 w-3" />
                  <span>Web</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {webSearchEnabled
                  ? 'AI будет искать информацию в интернете'
                  : 'Включить поиск в интернете'}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Разделитель перед остальными индикаторами */}
          {(selectedDocuments.length > 0 || hasReadyAttachments) && (
            <span className="text-muted-foreground/30">•</span>
          )}

          {/* Индикатор Knowledge Base */}
          {selectedDocuments.length > 0 && (
            <div className="inline-flex items-center gap-1 h-6 px-1.5 rounded-md bg-muted text-muted-foreground">
              <BookOpen className="h-3 w-3" />
              <span className="text-[10px]">{selectedDocuments.length} док.</span>
            </div>
          )}

          {/* Индикатор прикреплённых файлов */}
          {hasReadyAttachments && (
            <div className="inline-flex items-center gap-1 h-6 px-1.5 rounded-md bg-muted text-muted-foreground">
              <Paperclip className="h-3 w-3" />
              <span className="text-[10px]">{attachments.filter(a => a.status === 'ready').length}</span>
            </div>
          )}
        </div>

        {/* Правая часть: индикатор контекста - показывается только если есть сообщения */}
        {totalTokensUsed > 0 && (
          <button
            type="button"
            onClick={onClearContext}
            disabled={isLoading}
            className={cn(
              'relative h-6 w-6 rounded-full flex-shrink-0',
              'hover:opacity-80 transition-opacity disabled:opacity-50',
              'group'
            )}
            title={`Контекст: ${Math.round(contextUsagePercent)}% (${formatTokens(totalTokensUsed)} / ${formatTokens(contextLimit)}). Нажмите чтобы очистить.`}
          >
            {/* Фон круга */}
            <svg className="h-6 w-6 -rotate-90" viewBox="0 0 24 24">
              {/* Фоновый круг */}
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-muted/30"
              />
              {/* Прогресс-круг */}
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 10}
                strokeDashoffset={2 * Math.PI * 10 * (1 - Math.min(100, contextUsagePercent) / 100)}
                className="text-foreground transition-all duration-300"
              />
            </svg>
            {/* Процент в центре */}
            <span className="absolute inset-0 flex items-center justify-center text-[8px] font-medium text-foreground">
              {Math.round(contextUsagePercent)}
            </span>
          </button>
        )}
      </div>

      {/* Модальное окно выбора модели */}
      <ModelSelectorModal
        open={isModelModalOpen}
        onOpenChange={setIsModelModalOpen}
        selectedModel={selectedModel ?? null}
        onSelect={(model) => onModelChange?.(model)}
        routerEnabled={routerEnabled}
        onRouterEnabledChange={(enabled) => onRouterEnabledChange?.(enabled)}
        routerModel={routerModel ?? null}
        onRouterModelChange={(model) => onRouterModelChange?.(model)}
        webSearchEnabled={webSearchEnabled}
        onWebSearchEnabledChange={(enabled) => onWebSearchEnabledChange?.(enabled)}
        selectedDocuments={selectedDocuments}
        onSelectedDocumentsChange={(ids) => onSelectedDocumentsChange?.(ids)}
      />
    </div>
  );
}
